// IndustryTabRenderers.tsx - Shared rendering functions for industry tabs
import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  alpha,
  useTheme,
  Grid,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Check as CheckIcon
} from '@mui/icons-material';

// Editable inline text component
export const EditableInlineText = ({ value, onSave, multiline = false, placeholder = 'Click to edit...' }: any) => {
  const theme = useTheme();
  // Auto-edit mode for empty values
  const [isEditing, setIsEditing] = useState(!value || value.trim() === '');
  const [localValue, setLocalValue] = useState(value || '');

  const handleSave = () => {
    onSave(localValue);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <TextField
        fullWidth
        multiline={multiline}
        rows={multiline ? 3 : 1}
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={handleSave}
        autoFocus
        size="small"
        placeholder={placeholder}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 1,
            bgcolor: 'background.paper'
          }
        }}
      />
    );
  }

  return (
    <Box
      onClick={() => setIsEditing(true)}
      sx={{
        cursor: 'pointer',
        '&:hover': {
          bgcolor: alpha(theme.palette.primary.main, 0.05),
          borderRadius: 1
        },
        px: 1,
        py: 0.5
      }}
    >
      <Typography variant="body1" sx={{ minHeight: '24px' }}>
        {value || <span style={{ color: theme.palette.text.disabled, fontStyle: 'italic' }}>{placeholder}</span>}
      </Typography>
    </Box>
  );
};

// Editable list item component
export const EditableListItem = ({ value, onSave, onDelete }: any) => {
  const theme = useTheme();
  // Auto-edit mode for empty values
  const [isEditing, setIsEditing] = useState(!value || value.trim() === '');
  const [localValue, setLocalValue] = useState(value);

  const handleSave = () => {
    onSave(localValue);
    setIsEditing(false);
  };

  return (
    <ListItem
      sx={{
        py: 0.5,
        '&:hover .delete-btn': {
          opacity: 1
        }
      }}
    >
      <ListItemIcon sx={{ minWidth: 32 }}>
        <CheckIcon color="primary" fontSize="small" />
      </ListItemIcon>
      {isEditing ? (
        <Box sx={{ flex: 1, display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            multiline
            rows={2}
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={handleSave}
            autoFocus
            size="small"
            placeholder="Type here..."
          />
        </Box>
      ) : (
        <ListItemText
          primary={value || <span style={{ color: theme.palette.text.disabled, fontStyle: 'italic' }}>Click to edit...</span>}
          onClick={() => setIsEditing(true)}
          sx={{
            cursor: 'pointer',
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.02)
            },
            borderRadius: 1,
            px: 1
          }}
        />
      )}
      <IconButton
        size="small"
        onClick={onDelete}
        className="delete-btn"
        sx={{ opacity: 0, transition: 'opacity 0.2s' }}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </ListItem>
  );
};

// Helper functions for tab data management
export const createTabHelpers = (industry: any, onChange: (field: string, value: any) => void) => {
  const updateTabData = (tabName: string, field: string | string[], value: any) => {
    const tabs = { ...industry.tabs };
    if (!tabs[tabName]) tabs[tabName] = {};

    if (Array.isArray(field)) {
      let current = tabs[tabName];
      for (let i = 0; i < field.length - 1; i++) {
        if (!current[field[i]]) current[field[i]] = {};
        current = current[field[i]];
      }
      current[field[field.length - 1]] = value;
    } else {
      tabs[tabName][field] = value;
    }

    onChange('tabs', tabs);
  };

  const addArrayItem = (tabName: string, field: string | string[], value: any = '') => {
    const tabs = { ...industry.tabs };
    if (!tabs[tabName]) tabs[tabName] = {};

    let current = tabs[tabName];
    if (Array.isArray(field)) {
      for (let i = 0; i < field.length - 1; i++) {
        if (!current[field[i]]) current[field[i]] = {};
        current = current[field[i]];
      }
      const lastField = field[field.length - 1];
      if (!current[lastField]) current[lastField] = [];
      current[lastField] = [...current[lastField], value];
    } else {
      if (!current[field]) current[field] = [];
      current[field] = [...current[field], value];
    }

    onChange('tabs', tabs);
  };

  const removeArrayItem = (tabName: string, field: string | string[], index: number) => {
    const tabs = { ...industry.tabs };
    if (!tabs[tabName]) return;

    let current = tabs[tabName];
    if (Array.isArray(field)) {
      for (let i = 0; i < field.length - 1; i++) {
        if (!current[field[i]]) return;
        current = current[field[i]];
      }
      const lastField = field[field.length - 1];
      if (current[lastField]) {
        current[lastField] = current[lastField].filter((_: any, i: number) => i !== index);
      }
    } else {
      if (current[field]) {
        current[field] = current[field].filter((_: any, i: number) => i !== index);
      }
    }

    onChange('tabs', tabs);
  };

  const updateArrayItem = (tabName: string, field: string | string[], index: number, value: any) => {
    const tabs = { ...industry.tabs };
    if (!tabs[tabName]) return;

    let current = tabs[tabName];
    if (Array.isArray(field)) {
      for (let i = 0; i < field.length - 1; i++) {
        if (!current[field[i]]) return;
        current = current[field[i]];
      }
      const lastField = field[field.length - 1];
      if (current[lastField]) {
        current[lastField][index] = value;
      }
    } else {
      if (current[field]) {
        current[field][index] = value;
      }
    }

    onChange('tabs', tabs);
  };

  return { updateTabData, addArrayItem, removeArrayItem, updateArrayItem };
};

// Market Overview Tab Renderer
export const RenderMarketOverview: React.FC<{ industry: any; onChange: (field: string, value: any) => void }> = ({ industry, onChange }) => {
  const theme = useTheme();
  const data = industry.tabs?.marketOverview || {};
  const { updateTabData, addArrayItem, removeArrayItem, updateArrayItem } = createTabHelpers(industry, onChange);

  return (
    <Box>
      <Typography variant="h5" gutterBottom fontWeight={600}>
        Market Overview
      </Typography>

      {/* Market Potential */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" fontWeight={600} color="primary.main" gutterBottom>
          Market Potential
        </Typography>
        <List sx={{ mt: 2 }}>
          {(data.marketPotential || []).map((item: string, index: number) => (
            <EditableListItem
              key={index}
              value={item}
              onSave={(value: string) => updateArrayItem('marketOverview', 'marketPotential', index, value)}
              onDelete={() => removeArrayItem('marketOverview', 'marketPotential', index)}
            />
          ))}
        </List>
        <Button
          startIcon={<AddIcon />}
          onClick={() => addArrayItem('marketOverview', 'marketPotential', '')}
          sx={{ mt: 1, textTransform: 'none' }}
        >
          Add Market Potential Point
        </Button>
      </Box>

      {/* Critical Gaps */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" fontWeight={600} color="primary.main" gutterBottom>
          Critical Gaps
        </Typography>
        <List sx={{ mt: 2 }}>
          {(data.criticalGaps || []).map((item: string, index: number) => (
            <EditableListItem
              key={index}
              value={item}
              onSave={(value: string) => updateArrayItem('marketOverview', 'criticalGaps', index, value)}
              onDelete={() => removeArrayItem('marketOverview', 'criticalGaps', index)}
            />
          ))}
        </List>
        <Button
          startIcon={<AddIcon />}
          onClick={() => addArrayItem('marketOverview', 'criticalGaps', '')}
          sx={{ mt: 1, textTransform: 'none' }}
        >
          Add Critical Gap
        </Button>
      </Box>

      {/* Critical Strengths */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" fontWeight={600} color="primary.main" gutterBottom>
          Critical Strengths
        </Typography>
        <List sx={{ mt: 2 }}>
          {(data.criticalStrengths || []).map((item: string, index: number) => (
            <EditableListItem
              key={index}
              value={item}
              onSave={(value: string) => updateArrayItem('marketOverview', 'criticalStrengths', index, value)}
              onDelete={() => removeArrayItem('marketOverview', 'criticalStrengths', index)}
            />
          ))}
        </List>
        <Button
          startIcon={<AddIcon />}
          onClick={() => addArrayItem('marketOverview', 'criticalStrengths', '')}
          sx={{ mt: 1, textTransform: 'none' }}
        >
          Add Critical Strength
        </Button>
      </Box>

      {/* Gaps Blocking Growth */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" fontWeight={600} color="primary.main" gutterBottom>
          Gaps Blocking Growth
        </Typography>
        <List sx={{ mt: 2 }}>
          {(data.gapsBlockingGrowth || []).map((item: string, index: number) => (
            <EditableListItem
              key={index}
              value={item}
              onSave={(value: string) => updateArrayItem('marketOverview', 'gapsBlockingGrowth', index, value)}
              onDelete={() => removeArrayItem('marketOverview', 'gapsBlockingGrowth', index)}
            />
          ))}
        </List>
        <Button
          startIcon={<AddIcon />}
          onClick={() => addArrayItem('marketOverview', 'gapsBlockingGrowth', '')}
          sx={{ mt: 1, textTransform: 'none' }}
        >
          Add Gap Blocking Growth
        </Button>
      </Box>

      {/* Investment Shortfalls */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" fontWeight={600} color="primary.main" gutterBottom>
          Investment Shortfalls
        </Typography>
        <List sx={{ mt: 2 }}>
          {(data.investmentShortfalls || []).map((item: string, index: number) => (
            <EditableListItem
              key={index}
              value={item}
              onSave={(value: string) => updateArrayItem('marketOverview', 'investmentShortfalls', index, value)}
              onDelete={() => removeArrayItem('marketOverview', 'investmentShortfalls', index)}
            />
          ))}
        </List>
        <Button
          startIcon={<AddIcon />}
          onClick={() => addArrayItem('marketOverview', 'investmentShortfalls', '')}
          sx={{ mt: 1, textTransform: 'none' }}
        >
          Add Investment Shortfall
        </Button>
      </Box>

      {/* Bottom Line */}
      <Box sx={{ mt: 4, p: 3, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 2 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Bottom Line
        </Typography>
        <EditableInlineText
          value={data.bottomLine?.text}
          onSave={(value: string) => updateTabData('marketOverview', ['bottomLine', 'text'], value)}
          multiline
          placeholder="Click to add bottom line summary..."
        />
        <List sx={{ mt: 2 }}>
          {(data.bottomLine?.points || []).map((item: string, index: number) => (
            <EditableListItem
              key={index}
              value={item}
              onSave={(value: string) => updateArrayItem('marketOverview', ['bottomLine', 'points'], index, value)}
              onDelete={() => removeArrayItem('marketOverview', ['bottomLine', 'points'], index)}
            />
          ))}
        </List>
        <Button
          startIcon={<AddIcon />}
          onClick={() => addArrayItem('marketOverview', ['bottomLine', 'points'], '')}
          sx={{ mt: 1, textTransform: 'none' }}
        >
          Add Bottom Line Point
        </Button>
      </Box>
    </Box>
  );
};

// Legal & Infrastructure Tab Renderer
export const RenderLegalInfrastructure: React.FC<{ industry: any; onChange: (field: string, value: any) => void }> = ({ industry, onChange }) => {
  const theme = useTheme();
  const data = industry.tabs?.legalInfrastructure || {};
  const { updateTabData, addArrayItem, removeArrayItem, updateArrayItem } = createTabHelpers(industry, onChange);

  return (
    <Box>
      <Typography variant="h5" gutterBottom fontWeight={600}>
        Legal & Infrastructure
      </Typography>

      {/* Legal Section */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" fontWeight={600} color="primary.main" gutterBottom>
          Legal Framework
        </Typography>

        {/* Regulatory Links */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Regulatory Links
          </Typography>
          {(data.legal?.regulatoryLinks || []).map((link: any, index: number) => (
            <Paper key={index} sx={{ p: 2, mb: 2, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Link Text"
                    value={link.text || ''}
                    onChange={(e) => {
                      const newLinks = [...(data.legal?.regulatoryLinks || [])];
                      newLinks[index] = { ...newLinks[index], text: e.target.value };
                      updateTabData('legalInfrastructure', ['legal', 'regulatoryLinks'], newLinks);
                    }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="URL"
                    value={link.url || ''}
                    onChange={(e) => {
                      const newLinks = [...(data.legal?.regulatoryLinks || [])];
                      newLinks[index] = { ...newLinks[index], url: e.target.value };
                      updateTabData('legalInfrastructure', ['legal', 'regulatoryLinks'], newLinks);
                    }}
                    size="small"
                  />
                </Grid>
              </Grid>
              <IconButton
                size="small"
                color="error"
                onClick={() => removeArrayItem('legalInfrastructure', ['legal', 'regulatoryLinks'], index)}
                sx={{ mt: 1 }}
              >
                <DeleteIcon />
              </IconButton>
            </Paper>
          ))}
          <Button
            startIcon={<AddIcon />}
            onClick={() => addArrayItem('legalInfrastructure', ['legal', 'regulatoryLinks'], { text: '', url: '' })}
            sx={{ textTransform: 'none' }}
          >
            Add Regulatory Link
          </Button>
        </Box>

        {/* Legal Notes */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Legal Notes
          </Typography>
          <List>
            {(data.legal?.notes || []).map((note: string, index: number) => (
              <EditableListItem
                key={index}
                value={note}
                onSave={(value: string) => updateArrayItem('legalInfrastructure', ['legal', 'notes'], index, value)}
                onDelete={() => removeArrayItem('legalInfrastructure', ['legal', 'notes'], index)}
              />
            ))}
          </List>
          <Button
            startIcon={<AddIcon />}
            onClick={() => addArrayItem('legalInfrastructure', ['legal', 'notes'], '')}
            sx={{ textTransform: 'none' }}
          >
            Add Legal Note
          </Button>
        </Box>

        {/* Foundations */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Foundations
          </Typography>
          <List>
            {(data.legal?.foundations || []).map((foundation: string, index: number) => (
              <EditableListItem
                key={index}
                value={foundation}
                onSave={(value: string) => updateArrayItem('legalInfrastructure', ['legal', 'foundations'], index, value)}
                onDelete={() => removeArrayItem('legalInfrastructure', ['legal', 'foundations'], index)}
              />
            ))}
          </List>
          <Button
            startIcon={<AddIcon />}
            onClick={() => addArrayItem('legalInfrastructure', ['legal', 'foundations'], '')}
            sx={{ textTransform: 'none' }}
          >
            Add Foundation
          </Button>
        </Box>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Infrastructure Section */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" fontWeight={600} color="primary.main" gutterBottom>
          Infrastructure
        </Typography>

        {/* Physical Infrastructure */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Physical Infrastructure
          </Typography>
          <List>
            {(data.infrastructure?.physical || []).map((item: string, index: number) => (
              <EditableListItem
                key={index}
                value={item}
                onSave={(value: string) => updateArrayItem('legalInfrastructure', ['infrastructure', 'physical'], index, value)}
                onDelete={() => removeArrayItem('legalInfrastructure', ['infrastructure', 'physical'], index)}
              />
            ))}
          </List>
          <Button
            startIcon={<AddIcon />}
            onClick={() => addArrayItem('legalInfrastructure', ['infrastructure', 'physical'], '')}
            sx={{ textTransform: 'none' }}
          >
            Add Physical Infrastructure
          </Button>
        </Box>

        {/* Digital Infrastructure */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Digital Infrastructure
          </Typography>
          <List>
            {(data.infrastructure?.digital || []).map((item: string, index: number) => (
              <EditableListItem
                key={index}
                value={item}
                onSave={(value: string) => updateArrayItem('legalInfrastructure', ['infrastructure', 'digital'], index, value)}
                onDelete={() => removeArrayItem('legalInfrastructure', ['infrastructure', 'digital'], index)}
              />
            ))}
          </List>
          <Button
            startIcon={<AddIcon />}
            onClick={() => addArrayItem('legalInfrastructure', ['infrastructure', 'digital'], '')}
            sx={{ textTransform: 'none' }}
          >
            Add Digital Infrastructure
          </Button>
        </Box>

        {/* Priorities */}
        <Box sx={{ mt: 3, p: 3, bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Infrastructure Priorities
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Introduction"
            value={data.infrastructure?.priorities?.intro || ''}
            onChange={(e) => updateTabData('legalInfrastructure', ['infrastructure', 'priorities', 'intro'], e.target.value)}
            sx={{ mb: 2 }}
          />
          <List>
            {(data.infrastructure?.priorities?.items || []).map((item: string, index: number) => (
              <EditableListItem
                key={index}
                value={item}
                onSave={(value: string) => updateArrayItem('legalInfrastructure', ['infrastructure', 'priorities', 'items'], index, value)}
                onDelete={() => removeArrayItem('legalInfrastructure', ['infrastructure', 'priorities', 'items'], index)}
              />
            ))}
          </List>
          <Button
            startIcon={<AddIcon />}
            onClick={() => addArrayItem('legalInfrastructure', ['infrastructure', 'priorities', 'items'], '')}
            sx={{ textTransform: 'none' }}
          >
            Add Priority Item
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

// Opportunities & Incentives Tab Renderer
export const RenderIncentives: React.FC<{ industry: any; onChange: (field: string, value: any) => void }> = ({ industry, onChange }) => {
  const theme = useTheme();
  const data = industry.tabs?.incentives || {};
  const { updateTabData, addArrayItem, removeArrayItem } = createTabHelpers(industry, onChange);

  return (
    <Box>
      <Typography variant="h5" gutterBottom fontWeight={600}>
        Opportunities & Incentives
      </Typography>

      <TextField
        fullWidth
        label="Title"
        value={data.title || 'Incentives'}
        onChange={(e) => updateTabData('incentives', 'title', e.target.value)}
        sx={{ mt: 2, mb: 3 }}
      />

      <TextField
        fullWidth
        label="Content Title"
        value={data.content?.title || ''}
        onChange={(e) => updateTabData('incentives', ['content', 'title'], e.target.value)}
        sx={{ mb: 3 }}
      />

      {/* Sections */}
      <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mt: 3 }}>
        Sections
      </Typography>

      {(data.content?.sections || []).map((section: any, sectionIdx: number) => (
        <Paper
          key={sectionIdx}
          elevation={1}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
          }}
        >
          <TextField
            fullWidth
            label="Section Title"
            value={section.title || ''}
            onChange={(e) => {
              const newSections = [...(data.content?.sections || [])];
              newSections[sectionIdx].title = e.target.value;
              updateTabData('incentives', ['content', 'sections'], newSections);
            }}
            sx={{ mb: 2 }}
          />

          {/* Items with subsections */}
          {section.items && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Items
              </Typography>
              {section.items.map((item: any, itemIdx: number) => (
                <Paper key={itemIdx} sx={{ p: 2, mb: 2, bgcolor: alpha(theme.palette.background.default, 0.5) }}>
                  <TextField
                    fullWidth
                    label="Item Title"
                    value={item.title || ''}
                    onChange={(e) => {
                      const newSections = [...(data.content?.sections || [])];
                      newSections[sectionIdx].items[itemIdx].title = e.target.value;
                      updateTabData('incentives', ['content', 'sections'], newSections);
                    }}
                    size="small"
                    sx={{ mb: 2 }}
                  />

                  {/* Subsections */}
                  {item.subsections && item.subsections.map((subsection: any, subIdx: number) => (
                    <Box key={subIdx} sx={{ ml: 2, mb: 2 }}>
                      <TextField
                        fullWidth
                        label="Subsection Name"
                        value={subsection.name || ''}
                        onChange={(e) => {
                          const newSections = [...(data.content?.sections || [])];
                          newSections[sectionIdx].items[itemIdx].subsections[subIdx].name = e.target.value;
                          updateTabData('incentives', ['content', 'sections'], newSections);
                        }}
                        size="small"
                        sx={{ mb: 1 }}
                      />

                      {/* Subsection Details */}
                      {subsection.details && subsection.details.map((detail: string, detailIdx: number) => (
                        <TextField
                          key={detailIdx}
                          fullWidth
                          multiline
                          rows={2}
                          value={detail}
                          onChange={(e) => {
                            const newSections = [...(data.content?.sections || [])];
                            newSections[sectionIdx].items[itemIdx].subsections[subIdx].details[detailIdx] = e.target.value;
                            updateTabData('incentives', ['content', 'sections'], newSections);
                          }}
                          size="small"
                          sx={{ mb: 1, ml: 2 }}
                        />
                      ))}

                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          const newSections = [...(data.content?.sections || [])];
                          if (!newSections[sectionIdx].items[itemIdx].subsections[subIdx].details)
                            newSections[sectionIdx].items[itemIdx].subsections[subIdx].details = [];
                          newSections[sectionIdx].items[itemIdx].subsections[subIdx].details.push('');
                          updateTabData('incentives', ['content', 'sections'], newSections);
                        }}
                        sx={{ ml: 2 }}
                      >
                        Add Detail
                      </Button>

                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          const newSections = [...(data.content?.sections || [])];
                          newSections[sectionIdx].items[itemIdx].subsections = newSections[sectionIdx].items[itemIdx].subsections.filter(
                            (_: any, idx: number) => idx !== subIdx
                          );
                          updateTabData('incentives', ['content', 'sections'], newSections);
                        }}
                        sx={{ ml: 1 }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}

                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const newSections = [...(data.content?.sections || [])];
                      if (!newSections[sectionIdx].items[itemIdx].subsections)
                        newSections[sectionIdx].items[itemIdx].subsections = [];
                      newSections[sectionIdx].items[itemIdx].subsections.push({ name: '', details: [] });
                      updateTabData('incentives', ['content', 'sections'], newSections);
                    }}
                    sx={{ ml: 2 }}
                  >
                    Add Subsection
                  </Button>

                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => {
                      const newSections = [...(data.content?.sections || [])];
                      newSections[sectionIdx].items = newSections[sectionIdx].items.filter(
                        (_: any, idx: number) => idx !== itemIdx
                      );
                      updateTabData('incentives', ['content', 'sections'], newSections);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Paper>
              ))}
              <Button
                startIcon={<AddIcon />}
                onClick={() => {
                  const newSections = [...(data.content?.sections || [])];
                  if (!newSections[sectionIdx].items) newSections[sectionIdx].items = [];
                  newSections[sectionIdx].items.push({ title: '', subsections: [] });
                  updateTabData('incentives', ['content', 'sections'], newSections);
                }}
              >
                Add Item
              </Button>
            </Box>
          )}

          {/* Direct points without items */}
          {section.points && !section.items && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Points
              </Typography>
              {section.points.map((point: string, pointIdx: number) => (
                <TextField
                  key={pointIdx}
                  fullWidth
                  multiline
                  rows={2}
                  value={point}
                  onChange={(e) => {
                    const newSections = [...(data.content?.sections || [])];
                    newSections[sectionIdx].points[pointIdx] = e.target.value;
                    updateTabData('incentives', ['content', 'sections'], newSections);
                  }}
                  size="small"
                  sx={{ mb: 1 }}
                />
              ))}
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => {
                  const newSections = [...(data.content?.sections || [])];
                  if (!newSections[sectionIdx].points) newSections[sectionIdx].points = [];
                  newSections[sectionIdx].points.push('');
                  updateTabData('incentives', ['content', 'sections'], newSections);
                }}
              >
                Add Point
              </Button>
            </Box>
          )}

          <IconButton
            size="small"
            color="error"
            onClick={() => {
              const newSections = (data.content?.sections || []).filter((_: any, idx: number) => idx !== sectionIdx);
              updateTabData('incentives', ['content', 'sections'], newSections);
            }}
            sx={{ mt: 2 }}
          >
            <DeleteIcon />
          </IconButton>
        </Paper>
      ))}

      <Button
        startIcon={<AddIcon />}
        onClick={() => {
          const newSections = [...(data.content?.sections || []), { title: '', items: [] }];
          updateTabData('incentives', ['content', 'sections'], newSections);
        }}
        variant="outlined"
      >
        Add Section
      </Button>
    </Box>
  );
};

// Trends Tab Renderer
export const RenderTrends: React.FC<{ industry: any; onChange: (field: string, value: any) => void }> = ({ industry, onChange }) => {
  const theme = useTheme();
  const data = industry.tabs?.trends || {};
  const { updateTabData, addArrayItem, removeArrayItem, updateArrayItem } = createTabHelpers(industry, onChange);

  return (
    <Box>
      <Typography variant="h5" gutterBottom fontWeight={600}>
        Trends
      </Typography>

      {/* Trends List */}
      <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mt: 3 }}>
        Trend Categories
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 3, mt: 2 }}>
        {(data.trendsList || []).map((trend: any, trendIdx: number) => (
          <Paper
            key={trendIdx}
            elevation={1}
            sx={{
              p: 3,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
            }}
          >
            <TextField
              fullWidth
              label="Trend Name"
              value={trend.name || ''}
              onChange={(e) => {
                const newTrends = [...(data.trendsList || [])];
                newTrends[trendIdx] = { ...newTrends[trendIdx], name: e.target.value };
                updateTabData('trends', 'trendsList', newTrends);
              }}
              sx={{ mb: 2 }}
            />

            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Points
            </Typography>
            {(trend.points || []).map((point: string, pointIdx: number) => (
              <TextField
                key={pointIdx}
                fullWidth
                multiline
                rows={3}
                value={point}
                onChange={(e) => {
                  const newTrends = [...(data.trendsList || [])];
                  newTrends[trendIdx].points[pointIdx] = e.target.value;
                  updateTabData('trends', 'trendsList', newTrends);
                }}
                size="small"
                sx={{ mb: 1 }}
                placeholder="Title: Description format"
              />
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                const newTrends = [...(data.trendsList || [])];
                if (!newTrends[trendIdx].points) newTrends[trendIdx].points = [];
                newTrends[trendIdx].points.push('');
                updateTabData('trends', 'trendsList', newTrends);
              }}
            >
              Add Point
            </Button>

            <IconButton
              size="small"
              color="error"
              onClick={() => {
                const newTrends = (data.trendsList || []).filter((_: any, idx: number) => idx !== trendIdx);
                updateTabData('trends', 'trendsList', newTrends);
              }}
              sx={{ mt: 1 }}
            >
              <DeleteIcon />
            </IconButton>
          </Paper>
        ))}
      </Box>

      <Button
        startIcon={<AddIcon />}
        onClick={() => {
          const newTrends = [...(data.trendsList || []), { name: '', points: [] }];
          updateTabData('trends', 'trendsList', newTrends);
        }}
        variant="outlined"
        sx={{ mt: 2 }}
      >
        Add Trend Category
      </Button>

      {/* Where To Start */}
      <Box sx={{ mt: 4, p: 3, bgcolor: alpha(theme.palette.success.light, 0.1), borderRadius: 2 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom color="success.dark">
          Where To Start
        </Typography>
        <List>
          {(data.whereToStart || []).map((item: string, index: number) => (
            <EditableListItem
              key={index}
              value={item}
              onSave={(value: string) => updateArrayItem('trends', 'whereToStart', index, value)}
              onDelete={() => removeArrayItem('trends', 'whereToStart', index)}
            />
          ))}
        </List>
        <Button
          startIcon={<AddIcon />}
          onClick={() => addArrayItem('trends', 'whereToStart', '')}
          size="small"
        >
          Add Starting Point
        </Button>
      </Box>
    </Box>
  );
};

// Technology Tab Renderer
export const RenderTechnology: React.FC<{ industry: any; onChange: (field: string, value: any) => void }> = ({ industry, onChange }) => {
  const theme = useTheme();
  const data = industry.tabs?.technology || {};
  const { updateTabData, addArrayItem, removeArrayItem, updateArrayItem } = createTabHelpers(industry, onChange);

  // Helper to add a new asset
  const addAsset = () => {
    const newAsset = {
      name: '',
      description: '',
      capabilities: []
    };
    addArrayItem('technology', 'assets', newAsset);
  };

  // Helper to update an asset field
  const updateAsset = (index: number, field: string, value: any) => {
    const tabs = { ...industry.tabs };
    if (!tabs.technology) tabs.technology = {};
    if (!tabs.technology.assets) tabs.technology.assets = [];

    const assets = [...tabs.technology.assets];
    assets[index] = { ...assets[index], [field]: value };
    tabs.technology.assets = assets;

    onChange('tabs', tabs);
  };

  // Helper to add capability to an asset
  const addCapability = (assetIndex: number) => {
    const tabs = { ...industry.tabs };
    if (!tabs.technology?.assets?.[assetIndex]?.capabilities) {
      if (!tabs.technology) tabs.technology = {};
      if (!tabs.technology.assets) tabs.technology.assets = [];
      if (!tabs.technology.assets[assetIndex]) tabs.technology.assets[assetIndex] = { capabilities: [] };
      if (!tabs.technology.assets[assetIndex].capabilities) tabs.technology.assets[assetIndex].capabilities = [];
    }

    const assets = [...tabs.technology.assets];
    assets[assetIndex].capabilities = [...(assets[assetIndex].capabilities || []), ''];
    tabs.technology.assets = assets;

    onChange('tabs', tabs);
  };

  // Helper to update a capability
  const updateCapability = (assetIndex: number, capabilityIndex: number, value: string) => {
    const tabs = { ...industry.tabs };
    const assets = [...(tabs.technology?.assets || [])];
    const capabilities = [...(assets[assetIndex]?.capabilities || [])];
    capabilities[capabilityIndex] = value;
    assets[assetIndex] = { ...assets[assetIndex], capabilities };
    tabs.technology.assets = assets;
    onChange('tabs', tabs);
  };

  // Helper to remove a capability
  const removeCapability = (assetIndex: number, capabilityIndex: number) => {
    const tabs = { ...industry.tabs };
    const assets = [...(tabs.technology?.assets || [])];
    const capabilities = (assets[assetIndex]?.capabilities || []).filter((_: any, i: number) => i !== capabilityIndex);
    assets[assetIndex] = { ...assets[assetIndex], capabilities };
    tabs.technology.assets = assets;
    onChange('tabs', tabs);
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom fontWeight={600}>
        Technology
      </Typography>

      {/* Technology Assets */}
      <Box sx={{ mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight={600} color="secondary.main">
            Technology Assets
          </Typography>
          <Button
            startIcon={<AddIcon />}
            onClick={addAsset}
            variant="outlined"
            size="small"
            sx={{ textTransform: 'none' }}
          >
            Add Asset
          </Button>
        </Box>

        <Grid container spacing={3}>
          {(data.assets || []).map((asset: any, assetIndex: number) => (
            <Grid item xs={12} md={6} key={assetIndex}>
              <Paper
                elevation={1}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                  bgcolor: alpha(theme.palette.secondary.main, 0.02),
                  position: 'relative'
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => removeArrayItem('technology', 'assets', assetIndex)}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>

                {/* Asset Name */}
                <TextField
                  fullWidth
                  label="Asset Name"
                  value={asset.name || ''}
                  onChange={(e) => updateAsset(assetIndex, 'name', e.target.value)}
                  sx={{ mb: 2 }}
                  size="small"
                />

                {/* Asset Description */}
                <TextField
                  fullWidth
                  label="Description"
                  value={asset.description || ''}
                  onChange={(e) => updateAsset(assetIndex, 'description', e.target.value)}
                  multiline
                  rows={2}
                  sx={{ mb: 2 }}
                  size="small"
                />

                {/* Capabilities */}
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Capabilities
                </Typography>
                <List dense disablePadding>
                  {(asset.capabilities || []).map((capability: string, capIndex: number) => (
                    <EditableListItem
                      key={capIndex}
                      value={capability}
                      onSave={(value: string) => updateCapability(assetIndex, capIndex, value)}
                      onDelete={() => removeCapability(assetIndex, capIndex)}
                    />
                  ))}
                </List>
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => addCapability(assetIndex)}
                  size="small"
                  sx={{ mt: 1, textTransform: 'none' }}
                >
                  Add Capability
                </Button>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Emerging Technologies */}
      <Box sx={{ mt: 4, p: 3, bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 2, border: `1px solid ${alpha(theme.palette.info.main, 0.2)}` }}>
        <Typography variant="h6" fontWeight={600} gutterBottom color="info.main">
          Emerging & Future Technologies
        </Typography>

        <TextField
          fullWidth
          label="Title"
          value={data.emergingTechnologies?.title || ''}
          onChange={(e) => updateTabData('technology', ['emergingTechnologies', 'title'], e.target.value)}
          sx={{ mb: 2 }}
          size="small"
        />

        <TextField
          fullWidth
          label="Description"
          value={data.emergingTechnologies?.description || ''}
          onChange={(e) => updateTabData('technology', ['emergingTechnologies', 'description'], e.target.value)}
          multiline
          rows={2}
          sx={{ mb: 2 }}
          size="small"
        />

        <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ mt: 2 }}>
          Technologies List
        </Typography>
        <List>
          {(data.emergingTechnologies?.technologies || []).map((tech: string, index: number) => (
            <EditableListItem
              key={index}
              value={tech}
              onSave={(value: string) => updateArrayItem('technology', ['emergingTechnologies', 'technologies'], index, value)}
              onDelete={() => removeArrayItem('technology', ['emergingTechnologies', 'technologies'], index)}
            />
          ))}
        </List>
        <Button
          startIcon={<AddIcon />}
          onClick={() => addArrayItem('technology', ['emergingTechnologies', 'technologies'], '')}
          size="small"
        >
          Add Technology
        </Button>
      </Box>

      {/* Implementation Priorities */}
      <Box sx={{ mt: 4, p: 3, bgcolor: alpha(theme.palette.success.main, 0.05), borderRadius: 2, border: `1px solid ${alpha(theme.palette.success.main, 0.2)}` }}>
        <Typography variant="h6" fontWeight={600} gutterBottom color="success.dark">
          Technology Implementation Priorities
        </Typography>

        <TextField
          fullWidth
          label="Title"
          value={data.implementationPriorities?.title || ''}
          onChange={(e) => updateTabData('technology', ['implementationPriorities', 'title'], e.target.value)}
          sx={{ mb: 2 }}
          size="small"
        />

        <TextField
          fullWidth
          label="Intro Text"
          value={data.implementationPriorities?.intro || ''}
          onChange={(e) => updateTabData('technology', ['implementationPriorities', 'intro'], e.target.value)}
          multiline
          rows={2}
          sx={{ mb: 2 }}
          size="small"
        />

        <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ mt: 2 }}>
          Priorities List
        </Typography>
        <List>
          {(data.implementationPriorities?.priorities || []).map((priority: string, index: number) => (
            <EditableListItem
              key={index}
              value={priority}
              onSave={(value: string) => updateArrayItem('technology', ['implementationPriorities', 'priorities'], index, value)}
              onDelete={() => removeArrayItem('technology', ['implementationPriorities', 'priorities'], index)}
            />
          ))}
        </List>
        <Button
          startIcon={<AddIcon />}
          onClick={() => addArrayItem('technology', ['implementationPriorities', 'priorities'], '')}
          size="small"
        >
          Add Priority
        </Button>
      </Box>
    </Box>
  );
};
